/**
 * The person's tabs talk to each other (ADR-0075).
 *
 * Every tab of one person shares one server-side session (ADR-0046) but has
 * its own MSAL cache (ADR-0053). Four messages, over a `BroadcastChannel` —
 * in-memory messaging between same-origin tabs, nothing stored anywhere:
 *
 * - `who` / `account` — a tab with no account asks who is signed in; a
 *   signed-in tab answers with the account's username, which the asking tab
 *   passes to Microsoft as `loginHint` for its silent sign-in.
 * - `activity` — a tab saw a key press, click, touch or scroll; the others
 *   move their idle clocks, so the 10-minute limit counts the person.
 * - `ended` — a tab ended the session (sign-out, idle, refused renewal, or
 *   the server said so); the others go to the login page at once.
 *
 * A browser without `BroadcastChannel` behaves exactly as before: each tab on
 * its own.
 */

import type { EndReason } from "./session";

export type TabMessage =
  | { type: "who" }
  | { type: "account"; username: string }
  | { type: "activity"; at: number }
  | { type: "ended"; reason: EndReason | "session_ended" };

/** The part of `BroadcastChannel` this module uses; tests hand in a fake. */
export interface TabPort {
  postMessage(message: TabMessage): void;
  addEventListener(type: "message", listener: (event: MessageEvent<TabMessage>) => void): void;
  removeEventListener(
    type: "message",
    listener: (event: MessageEvent<TabMessage>) => void,
  ): void;
  close(): void;
}

const CHANNEL_NAME = "daquma-auth-tabs";
const ASK_TIMEOUT_MS = 300;

// `undefined` = not opened yet; `null` = this browser has no channel.
let port: TabPort | null | undefined;

function open(): TabPort | null {
  if (port !== undefined) return port;
  if (typeof BroadcastChannel === "undefined") {
    port = null;
    return port;
  }
  try {
    port = new BroadcastChannel(CHANNEL_NAME) as unknown as TabPort;
  } catch {
    port = null;
  }
  return port;
}

function isTabMessage(data: unknown): data is TabMessage {
  return typeof data === "object" && data !== null && typeof (data as { type?: unknown }).type === "string";
}

/** Whether the tabs can hear each other in this browser. */
export function tabsAvailable(): boolean {
  return open() !== null;
}

/** Tell the other tabs; a no-op without a channel. Never throws. */
export function postToTabs(message: TabMessage): void {
  const p = open();
  if (!p) return;
  try {
    p.postMessage(message);
  } catch {
    // A closed channel: nothing to tell.
  }
}

/** Listen to the other tabs; returns the unsubscribe. */
export function subscribeToTabs(listener: (message: TabMessage) => void): () => void {
  const p = open();
  if (!p) return () => undefined;
  const handler = (event: MessageEvent<TabMessage>) => {
    if (isTabMessage(event.data)) listener(event.data);
  };
  p.addEventListener("message", handler);
  return () => p.removeEventListener("message", handler);
}

/**
 * Ask the open tabs which account is signed in. Resolves with the first
 * answer's username, or `null` when nobody answers within the timeout — a
 * silent sign-in then goes ahead without a hint.
 */
export function askOtherTabsForAccount(timeoutMs = ASK_TIMEOUT_MS): Promise<string | null> {
  return new Promise((resolve) => {
    if (!tabsAvailable()) {
      resolve(null);
      return;
    }
    // The listener and the timer each cancel the other; the listener is
    // installed first so an immediate answer is not missed.
    const pending: { timer?: ReturnType<typeof setTimeout> } = {};
    const stop = subscribeToTabs((message) => {
      if (message.type === "account" && message.username) {
        if (pending.timer !== undefined) clearTimeout(pending.timer);
        stop();
        resolve(message.username);
      }
    });
    pending.timer = setTimeout(() => {
      stop();
      resolve(null);
    }, timeoutMs);
    postToTabs({ type: "who" });
  });
}

/** Test hook: install a fake port (`null` = no channel, `undefined` = reopen). */
export function _setTabPortForTests(fake: TabPort | null | undefined): void {
  port = fake;
}
